import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModelSelect } from "@/components/ModelSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorText } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_MODEL = "anthropic:claude-sonnet-5-5";

export const CreateProjectDialog = ({ open, onOpenChange, onCreated }) => {
  const nextYear = new Date().getFullYear() + 1;
  const [properties, setProperties] = useState([]);
  useEffect(() => { if (open) api.get("/properties").then((r) => setProperties(r.data.filter((p) => p.is_active))).catch(() => {}); }, [open]);
  const [form, setForm] = useState({ hotel_name: "", name: "", budget_year: nextYear, ai_model: DEFAULT_MODEL, currency: "IDR", budget_source: "generate" });
  const SOURCES = [
    { id: "generate", label: "Generate next-year budget from actuals", hint: "Upload this year's actual P&L; the tool forecasts the remaining months and builds next year's budget with your assumptions." },
    { id: "file", label: "Use the Budget column of the uploaded P&L (actual vs approved budget)", hint: "Upload the monthly P&L reports of the budget year itself; the Budget column becomes the budget and the actuals feed Budget vs Actual, reforecast and flash reports immediately." },
  ];
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const name = form.name || `${form.hotel_name} - Budget ${form.budget_year}`;
      const { data } = await api.post("/projects", { ...form, name, budget_year: Number(form.budget_year) });
      toast.success("Budget project created");
      onCreated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="create-project-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">New Budget Project</DialogTitle>
          <DialogDescription>One project = one Armella Group property for one budget year.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div className="space-y-2">
            <Label>Property / Hotel</Label>
            <Select value={form.hotel_name} onValueChange={(v) => setForm({ ...form, hotel_name: v })} required>
              <SelectTrigger data-testid="hotel-name-select"><SelectValue placeholder="Select a registered property" /></SelectTrigger>
              <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.name} data-testid={`hotel-option-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{p.name}{p.code ? ` (${p.code})` : ""}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-slate-500">{properties.length ? "Only registered properties can be selected." : <>No registered properties yet. <Link to="/admin/properties" className="text-emerald-700 font-semibold hover:underline">Register properties</Link> first.</>}</p>
          </div>
          <div className="space-y-2">
            <Label>Budget Source</Label>
            <Select value={form.budget_source} onValueChange={(v) => setForm({ ...form, budget_source: v, budget_year: v === "file" ? nextYear - 1 : nextYear })}>
              <SelectTrigger data-testid="budget-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`budget-source-option-${o.id}`}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500" data-testid="budget-source-hint">{SOURCES.find((o) => o.id === form.budget_source)?.hint}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{form.budget_source === "file" ? "Budget Year (year of the P&L files)" : "Budget Year"}</Label>
              <Input type="number" data-testid="budget-year-input" value={form.budget_year} onChange={(e) => setForm({ ...form, budget_year: e.target.value })} min={2020} max={2100} required />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Input data-testid="currency-input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Project Name (optional)</Label>
            <Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={`${form.hotel_name || "Property"} - Budget ${form.budget_year}`} />
          </div>
          <div className="space-y-2">
            <Label>AI Model</Label>
            <ModelSelect value={form.ai_model} onChange={(v) => setForm({ ...form, ai_model: v })} />
            <p className="text-[11px] text-slate-500">Claude, ChatGPT or Gemini — used for structure detection, classification, narrative and flash summaries. Can be changed later on the project page.</p>
          </div>
          <Button type="submit" disabled={busy || !form.hotel_name} data-testid="create-project-submit-button" className="w-full bg-slate-900 hover:bg-emerald-700">
            {busy ? "Creating..." : "Create Project"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
